'use client'

import { useState, useEffect, useRef, useContext } from 'react'
import useFormContext from '@/context/useFormContext'
import { useForm, useFieldArray } from 'react-hook-form'
import { Heading } from '@/components/Heading'
import Input from '@/components/Input'
import { Select } from '@/components/Select'
import { Box } from '@/components/Box'
import { Button } from '@/components/Button'
import { Question } from '@/components/Question'
import { assetTypeOptions, recurringExpenseTypeOptions } from '@/data/selectOptions'
import { MinusCircleIcon } from '@heroicons/react/24/outline'
import Textarea from '@/components/Textarea'
import { Tooltip } from 'react-tooltip'
import { TooltipIcon } from '@/components/TooltipIcon'
import { PageLoader } from '@/components/PageLoader.tsx'
import { Header } from '@/components/HeaderPersonal'
import { ROUTES } from '@/config/routes'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { DealerContext } from '@/context/DealerContext'
import { ReactSVG } from 'react-svg'
import { useFormValidation } from '@/context/FormValidationContext'
import { useFormSubmission } from '@/hooks/useFormSubmission'
import { useFormSaveForLater } from '@/hooks/useFormSaveForLater'
import ReCAPTCHA, { ReCAPTCHAHandle } from '@steamer-academy/react-google-recaptcha'
import { preparePayload } from '@/services/saveService'
import { scrollToFirstError } from '@/utils/helper'
import { validateMoney } from '@/utils/validation'

function FinancialPosition() {
  const navigate = usePersistedNavigation()
  const { state, dispatch } = useFormContext()
  const dealerContext = useContext(DealerContext)
  const [totalMonthlyPayments, setTotalMonthlyPayments] = useState(0)
  const [totalRecurringMonthlyPayments, setTotalRecurringMonthlyPayments] = useState(0)
  const formRef = useRef<HTMLFormElement>(null)
  const recaptchaRef = useRef<ReCAPTCHAHandle>(null)
  const { register, watch, control, formState: { errors, isSubmitted }, handleSubmit, setValue, getValues, trigger } = useForm({
    defaultValues: {
      isOwnHome: state.personal.financialPosition.isOwnHome,
      mortgateValue: state.personal.financialPosition.mortgateValue,
      vehicleValue: state.personal.financialPosition.vehicleValue,
      furnitureValue: state.personal.financialPosition.furnitureValue,
      otherAssets: state.personal.financialPosition.otherAssets,
      creditCardAmount: state.personal.financialPosition.creditCardAmount,
      creditCardMonthlyPayment: state.personal.financialPosition.creditCardMonthlyPayment,
      mortgateBalance: state.personal.financialPosition.mortgateBalance,
      mortgateMonthlyPayment: state.personal.financialPosition.mortgateMonthlyPayment,
      mortgageRentPaymentAmount: state.personal.financialPosition.mortgageRentPaymentAmount,
      personalLoanAmount: state.personal.financialPosition.personalLoanAmount,
      personalLoanMonthlyPayment: state.personal.financialPosition.personalLoanMonthlyPayment,
      otherFinancialCommitments: state.personal.financialPosition.otherFinancialCommitments,
      councilRatesExpenditureValue: state.personal.financialPosition.councilRatesExpenditureValue,
      homeInsuranceExpenditureValue: state.personal.financialPosition.homeInsuranceExpenditureValue,
      utilitiesExpenditureValue: state.personal.financialPosition.utilitiesExpenditureValue,
      livingExpenditureValue: state.personal.financialPosition.livingExpenditureValue,
      vehicleExpenditureValue: state.personal.financialPosition.vehicleExpenditureValue,
      otherRecurringExpenses: state.personal.financialPosition.otherRecurringExpenses,
      additionalNotes: state.personal.financialPosition.additionalNotes
    }
  })
  const [isRecaptchaError, setIsRecaptchaError] = useState(false)
  const [submitAction, setSubmitAction] = useState<'submit' | 'continue' | null>(null)
  const [isPostCallChecked, setIsPostCallChecked] = useState(false)
  const [isPostCallError, setIsPostCallError] = useState(false)

  // We want to track state in ref for use in the triggerSave function which is passed as a callback on the mount
  const stateRef = useRef(state)
  useEffect(() => { stateRef.current = state }, [state])

  // Register all custom select components https://react-hook-form.com/faqs#Whatifyoudonthaveaccesstoref
  useEffect(() => {
    register('isOwnHome', { 
      validate: (value) => value === true || value === false || 'This field is required'
    })
  })

  const isJointApplication = state.personal.aboutYou.isJoint

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'otherAssets'
  })

  const {
    fields: financialCommitmentsFields,
    append: appendFinancialCommitments,
    remove: removeFinancialCommitments
  } = useFieldArray({
    control,
    name: 'otherFinancialCommitments'
  })

  const {
    fields: recurringExpensesFields,
    append: appendRecurringExpenses,
    remove: removeRecurringExpenses
  } = useFieldArray({
    control,
    name: 'otherRecurringExpenses'
  })

  const appendAndRegisterOtherAssets = () => {
    append({ assetType: '', amount: null })
    register(`otherAssets.${fields.length}.assetType`, { required: 'This field is required' })
  }

  const appendOtherFinancialCommitments = () => {
    appendFinancialCommitments({ description: '', amount: null, monthlyPayments: null })
  }

  const appendAndRegisterRecurringExpenses = () => {
    appendRecurringExpenses({ expenseType: '', amount: null })
    register(`otherRecurringExpenses.${recurringExpensesFields.length}.expenseType`, { required: 'This field is required' })
  }

  const { handleFormSave, handleFormSubmit, isLoading } = useFormSubmission({
    onSuccess: (data) => {
      console.log('Form submission successful:', data)
      // Redirect to the end application page
      if (submitAction === 'submit') {
        navigate(ROUTES.PERSONAL.END_APPLICATION.path)
      }
    },
    onError: (error) => {
      setRecaptchaToken(null)
      recaptchaRef.current?.reset()
      window.alert(error.message)
      console.error('Error during form submit:', error)
    }
  })

  const dispatchFormData = (data: any) => {
    console.log('Form dispatch:', data)
    dispatch({
      type: 'UPDATE_PERSONAL',
      step: 'financialPosition',
      payload: data.personal.financialPosition
    })
  }
  
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const bindFinancialPositionData = (data: any) => {
    const currentState = stateRef.current

    // Calculate totals for other liabilities
    const otherAmount = Array.isArray(data.otherFinancialCommitments)
      ? data.otherFinancialCommitments.reduce((sum: number, item: any) => sum + (parseFloat(item.amount) || 0), 0)
      : 0
    const otherMonthlyPayment = Array.isArray(data.otherFinancialCommitments)
      ? data.otherFinancialCommitments.reduce((sum: number, item: any) => sum + (parseFloat(item.monthlyPayments) || 0), 0)
      : 0

    return {
      ...currentState,
      business: null,
      personal: {
        ...currentState.personal,
        financialPosition: {
          isOwnHome: data.isOwnHome,
          mortgateValue: data.mortgateValue === '' ? null : data.mortgateValue,
          vehicleValue: data.vehicleValue === '' ? null : data.vehicleValue,
          furnitureValue: data.furnitureValue === '' ? null : data.furnitureValue,
          otherAssets: data.otherAssets,
          creditCardAmount: data.creditCardAmount === '' ? null : data.creditCardAmount,
          creditCardMonthlyPayment: data.creditCardMonthlyPayment === '' ? null : data.creditCardMonthlyPayment,
          mortgateBalance: data.mortgateBalance === '' ? null : data.mortgateBalance,
          mortgateMonthlyPayment: data.mortgateMonthlyPayment === '' ? null : data.mortgateMonthlyPayment,
          mortgageRentPaymentAmount: data.mortgageRentPaymentAmount === '' ? null : data.mortgageRentPaymentAmount,
          personalLoanAmount: data.personalLoanAmount === '' ? null : data.personalLoanAmount,
          personalLoanMonthlyPayment: data.personalLoanMonthlyPayment === '' ? null : data.personalLoanMonthlyPayment,
          otherFinancialCommitments: data.otherFinancialCommitments,
          otherAmount, // calculated
          otherMonthlyPayment, // calculated
          councilRatesExpenditureValue: data.councilRatesExpenditureValue === '' ? null : data.councilRatesExpenditureValue,
          homeInsuranceExpenditureValue: data.homeInsuranceExpenditureValue === '' ? null : data.homeInsuranceExpenditureValue,
          utilitiesExpenditureValue: data.utilitiesExpenditureValue === '' ? null : data.utilitiesExpenditureValue,
          livingExpenditureValue: data.livingExpenditureValue === '' ? null : data.livingExpenditureValue,
          vehicleExpenditureValue: data.vehicleExpenditureValue === '' ? null : data.vehicleExpenditureValue,
          otherRecurringExpenses: data.otherRecurringExpenses,
          additionalNotes: data.additionalNotes
        }
      }
    }
  }

  const onSubmit = (data: any) => {
    if (!submitAction) return

    // If isPostCall is true and user is submitting, require checkbox
    if (state.isPostCall && submitAction === 'submit' && !isPostCallChecked) {
      setIsPostCallError(true)
      return
    }
    setIsPostCallError(false)

    data = bindFinancialPositionData(data)

    if (submitAction === 'submit') {
      if (!recaptchaToken) {
        setIsRecaptchaError(true)
        return
      }
      
      dispatchFormData(data)
      handleFormSubmit(data, recaptchaToken)
    } else if (submitAction === 'continue') {
      dispatchFormData(data)
      handleFormSave(preparePayload(data, 'personal', 'financialPosition'), ROUTES.PERSONAL.DOC_UPLOAD.path)
    }
  }

  const handleSelectChange = (fieldName: string, value: string) => {
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    setValue(fieldName, value)
    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
    if (isSubmitted) trigger(fieldName)
  }

  const ownsHome = watch('isOwnHome') === true
  const creditCardMonthlyPayment = watch('creditCardMonthlyPayment') || 0
  const mortgateMonthlyPayment = watch('mortgateMonthlyPayment') || 0
  const rentMonthlyPayments = watch('mortgageRentPaymentAmount') || 0
  const personalLoanMonthlyPayment = watch('personalLoanMonthlyPayment') || 0
  const otherFinancialCommitments = watch('otherFinancialCommitments')

  const calculateTotalMonthlyPayments = () => {
    const otherAssetsTotal = otherFinancialCommitments.reduce((sum: number, asset: { monthlyPayments: string }) => {
      const amount = parseFloat(asset.monthlyPayments) || 0
      return sum + amount
    }, 0)

    const total = parseFloat(creditCardMonthlyPayment) +
      parseFloat(mortgateMonthlyPayment) +
      parseFloat(rentMonthlyPayments) +
      parseFloat(personalLoanMonthlyPayment) +
      otherAssetsTotal

    setTotalMonthlyPayments(total)
  }

  useEffect(() => {
    calculateTotalMonthlyPayments()
  }, [creditCardMonthlyPayment, mortgateMonthlyPayment, rentMonthlyPayments, personalLoanMonthlyPayment, otherFinancialCommitments, ])

  const councilRatesExpenditure = watch('councilRatesExpenditureValue') || 0
  const homeInsuranceExpenditure = watch('homeInsuranceExpenditureValue') || 0
  const utilitiesExpenditure = watch('utilitiesExpenditureValue') || 0
  const livingExpenditure = watch('livingExpenditureValue') || 0
  const vehicleExpenditure = watch('vehicleExpenditureValue') || 0
  const otherRecurringExpenses = watch('otherRecurringExpenses')

  const calculateTotalRecurringMonthlyPayments = () => {
    const otherRecurringExpensesTotal = otherRecurringExpenses.reduce((sum: number, expense: { monthlyPayments: string }) => {
      const amount = parseFloat(expense.monthlyPayments) || 0
      return sum + amount
    }, 0)

    const total = parseFloat(councilRatesExpenditure) +
      parseFloat(homeInsuranceExpenditure) +
      parseFloat(utilitiesExpenditure) +
      parseFloat(livingExpenditure) +
      parseFloat(vehicleExpenditure) +
      otherRecurringExpensesTotal

    setTotalRecurringMonthlyPayments(total)
  }

  useEffect(() => {
    calculateTotalRecurringMonthlyPayments()
  }, [councilRatesExpenditure, homeInsuranceExpenditure, utilitiesExpenditure, livingExpenditure, vehicleExpenditure, otherRecurringExpenses])

  const getSVGIcon = (icon: string, defaultIcon: string) => {
    console.log(dealerContext?.dealerThemeIcon.personalFinancialPosition)
    return dealerContext?.dealerThemeIcon.personalFinancialPosition.find(res => res.option === icon)?.icon || defaultIcon
  }

  const { registerValidationTrigger, unregisterValidationTrigger } = useFormValidation()
  const triggerFormValidation = () => handleSubmit(() => {}, (errors) => {
    if (errors.isOwnHome) window.scrollTo(0, 0)
  })()

  // Register the validation trigger when the component mounts
  useEffect(() => {
    registerValidationTrigger(triggerFormValidation)
    return () => unregisterValidationTrigger()
  }, [registerValidationTrigger, unregisterValidationTrigger])

  /* Handle saving for later */
  const { registerSaveCallback, unregisterSaveCallback } = useFormSaveForLater()
  const triggerSave = () => {
    const financialPositionData = getValues()
    const data = bindFinancialPositionData(financialPositionData)

    dispatchFormData(data)
    handleFormSave(preparePayload(data, 'personal', 'financialPosition'))
  }

  // Register the save form trigger when the component mounts
  useEffect(() => {
    registerSaveCallback(triggerSave)
    return () => unregisterSaveCallback()
  }, [registerSaveCallback, unregisterSaveCallback])
  /* End - Handle saving for later */

  const [isDealer, setIsDealer] = useState(false)
  useEffect(() => {
    if (dealerContext?.dealerInformation?.dealerUniqueIdentifier) {
      setIsDealer(true)
    }
  }, [dealerContext])

  return (
    <>
      <Header />
      <form ref={formRef} onSubmit={handleSubmit(onSubmit, (errors) => {
        // Need to scroll to the top as that is a custom component and Use React Form scrolls
        // only to native form elements
        if (errors.isOwnHome) window.scrollTo(0, 0)

        // Add this line to scroll to first error if there are any
        if (Object.keys(errors).length > 0) {
          scrollToFirstError()
        }
      })}>
        <section className='pb-12'>
          <Tooltip className='Tooltip Tooltip--info' id='tooltip-holder' />
          {/* Please leave on one line */}
          <Heading><span>Tell us about your {isJointApplication && <span>combined</span>} <span className='whitespace-nowrap'>assets <TooltipIcon id='tooltip-holder' content='Assets are anything of substantial monetary value which you own. E.g cash savings, property or investments' /></span></span></Heading>

          <Question
            title='Do you rent or own your home? *'
            name='isOwnHome'
            defaultValue={state.personal.financialPosition.isOwnHome}
            onChange={value => handleSelectChange('isOwnHome', value)}
            error={errors.isOwnHome}
            options={[
              { id: 'isOwnHomeYes', title: 'Own', value: true },
              { id: 'isOwnHomeNo', title: 'Rent', value: false }
            ]}
          />

          <Box>
            <div>
              <div className='grid grid-cols-1 lg:grid-cols-3 border-b border-[var(--form-box-divider)] justify-end mb-0 max-lg:hidden'>
                <p className='col-start-3 pb-3 text-sm font-[var(--font-weight-bold)] pl-6'>Value</p>
              </div>
              {ownsHome && (
                <div className='grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='lg:col-span-2 pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/house.svg' aria-label='Home'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('home', '/icons/financial-position/house.svg')} aria-label='Home'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5'>Home</p>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0 *'
                    inputId='mortgateValue'
                    required={true}
                    money={true}
                    {...register('mortgateValue', {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    error={errors.mortgateValue}
                  />
                </div>
              )}

              <div className='grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-2 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/vehicles.svg' aria-label='vehicles'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('vehicles', '/icons/financial-position/vehicles.svg')} aria-label='vehicles'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Vehicles</p>
                  </div>
                </div>
                <Input
                  label='Value'
                  placeholder='0'
                  inputId='vehicleValue'
                  money={true}
                  {...register('vehicleValue', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.vehicleValue}
                />
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-2 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/furniture.svg' aria-label='Furniture and household items'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('furnitureAndHouseholdItems', '/icons/financial-position/furniture.svg')} aria-label='Furniture and household items'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Furniture and household items</p>
                  </div>
                </div>
                <Input
                  label='Value'
                  placeholder='0'
                  inputId='furnitureValue'
                  money={true}
                  {...register('furnitureValue', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.furnitureValue}
                />
              </div>

              {fields.map((field, index) => (
                <div key={field.id} className='grid relative grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-16 mb-2 last:mb-0 lg:mb-0'>
                  <div className='lg:col-span-2 pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 flex items-center justify-center max-lg:mt-6 text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/menu.svg' aria-label='true'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src='/icons/financial-position/menu.svg' aria-hidden='true'/>
                        }
                      </span>
                      <div className='grow -mt-2'>
                        <Select
                          label='Asset'
                          required={true}
                          selectOptions={assetTypeOptions}
                          defaultValue={state.personal.financialPosition.otherAssets[index]?.assetType}
                          onChange={value => handleSelectChange(`otherAssets.${index}.assetType`, value)}
                          // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                          error={errors.otherAssets?.[index]?.assetType}
                        />
                      </div>
                    </div>
                  </div>
                  <Input
                    label='Value'
                    placeholder='0 *'
                    inputId={`otherAssets[${index}]amount`}
                    required={true}
                    money={true}
                    {...register(`otherAssets.${index}.amount`, {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                    error={errors.otherAssets?.[index]?.amount}
                  />
                  <button
                    type='button'
                    className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full absolute z-10 bottom-4 right-0 p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)]'
                    onClick={() => remove(index)}
                  >
                    <MinusCircleIcon className='w-6 h-6' />
                  </button>
                </div>
              ))}

              <div className='flex justify-end pt-2 mb-0'>
                <Button
                  icon='add'
                  style='text'
                  onClick={appendAndRegisterOtherAssets}
                >Add new</Button>
              </div>
            </div>
          </Box>

          {/* Please leave on one line */}
          <Heading><span>What are your {isJointApplication && <span>combined</span>} financial <span className='whitespace-nowrap'>commitments <TooltipIcon id='tooltip-holder' content='Tell us about any ongoing financial commitment that you have. E.g mortgage or credit card payments, student loans or any personal loans' /></span></span></Heading>
          <Box>
            <div>
              <div className='grid grid-cols-1 lg:grid-cols-4 border-b lg:gap-8 border-[var(--form-box-divider)] justify-end mb-0 max-lg:hidden'>
                <p className='col-start-3 pb-3 text-sm font-[var(--font-weight-bold)]'>Limit</p>
                <p className='col-start-4 pb-3 text-sm font-[var(--font-weight-bold)]'>Monthly payments</p>
              </div>
              <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-2 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/credit-card.svg' aria-label='Credit card'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('creditCards', '/icons/financial-position/credit-card.svg')} aria-label='Credit card'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Credit cards <TooltipIcon id='tooltip-holder' content='Enter your credit card limit and the average amount you repay each month.' /></p>
                  </div>
                </div>
                <Input
                  label='Limit'
                  placeholder='0'
                  inputId='creditCardAmount'
                  money={true}
                  defaultValue={state.personal.financialPosition.creditCardAmount}
                  {...register('creditCardAmount', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.creditCardAmount}
                />
                <Input
                  label='Monthly payments'
                  placeholder='0'
                  inputId='creditCardMonthlyPayment'
                  money={true}
                  defaultValue={state.personal.financialPosition.creditCardMonthlyPayment}
                  {...register('creditCardMonthlyPayment', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.creditCardMonthlyPayment}
                />
              </div>
              <div className='grid grid-cols-1 lg:grid-cols-4 border-b lg:gap-8 border-[var(--form-box-divider)] justify-end pt-12 mb-0 max-lg:hidden'>
                <p className='col-start-3 pb-3 text-sm font-[var(--font-weight-bold)]'>Balance</p>
                <p className='col-start-4 pb-3 text-sm font-[var(--font-weight-bold)]'>Monthly payments</p>
              </div>

              {ownsHome && (
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='lg:col-span-2 pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/mortgage.svg' aria-label='Mortgage'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('mortgage', '/icons/financial-position/mortgage.svg')} aria-label='Mortgage'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5'>Mortgage <TooltipIcon id='tooltip-holder' content='Enter the amount remaining on your mortgage and the amount you repay each month.' /></p>
                    </div>
                  </div>
                  <Input
                    label='Limit'
                    placeholder='0 *'
                    inputId='mortgateBalance'
                    money={true}
                    defaultValue={state.personal.financialPosition.mortgateBalance}
                    {...register('mortgateBalance', {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    error={errors.mortgateBalance}
                  />
                  <Input
                    label='Monthly payments'
                    placeholder='0 *'
                    inputId='mortgateMonthlyPayment'
                    money={true}
                    defaultValue={state.personal.financialPosition.mortgateMonthlyPayment}
                    {...register('mortgateMonthlyPayment', {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    error={errors.mortgateMonthlyPayment}
                  />
                </div>
              )}

              {!ownsHome && (
                <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                  <div className='lg:col-span-2 pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/mortgage.svg' aria-label='mortgage'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('rent', '/icons/financial-position/mortgage.svg')} aria-label='vehicles'/>
                        }
                      </span>
                      <p className='text-lg/7 mt-0.5'>Rent <TooltipIcon id='tooltip-holder' content='Enter your monthly rent payment' /></p>
                    </div>
                  </div>

                  <div className='lg:col-start-4'>
                    <Input
                      label='Monthly payments'
                      placeholder='0 *'
                      inputId='mortgageRentPaymentAmount'
                      money={true}
                      defaultValue={state.personal.financialPosition.mortgageRentPaymentAmount}
                      {...register('mortgageRentPaymentAmount', {
                        required: 'This field is required',
                        validate: (value) => validateMoney(true, value)
                      })}
                      error={errors.mortgageRentPaymentAmount}
                    />
                  </div>
                </div>
              )}

              <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-2 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/loans.svg' aria-label='Loans'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('loans', '/icons/financial-position/loans.svg')} aria-label='Loans'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Loans <TooltipIcon id='tooltip-holder' content='Enter any other loans you have and the amount you pay on these each month. These may include personal loans, student loans, hire purchase or buy now pay later arrangements.' /></p>
                  </div>
                </div>
                <Input
                  label='Limit'
                  placeholder='0'
                  inputId='personalLoanAmount'
                  money={true}
                  defaultValue={state.personal.financialPosition.personalLoanAmount}
                  {...register('personalLoanAmount', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.personalLoanAmount}
                />
                <Input
                  label='Monthly payments'
                  placeholder='0'
                  inputId='personalLoanMonthlyPayment'
                  money={true}
                  defaultValue={state.personal.financialPosition.personalLoanMonthlyPayment}
                  {...register('personalLoanMonthlyPayment', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.personalLoanMonthlyPayment}
                />
              </div>

              {/* This will only display if add new is clicked */}
              {financialCommitmentsFields.map((field, index) => (
                <div key={field.id} className='grid relative grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-16 mb-2 last:mb-0 lg:mb-0'>
                  <div className='lg:col-span-2 pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 flex items-center justify-center max-lg:mt-6 text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/menu.svg' aria-label='true'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src='/icons/financial-position/menu.svg' aria-hidden='true'/>
                        }
                      </span>
                      <div className='grow -mt-2'>
                        <Input
                          label='Description'
                          placeholder='Description *'
                          inputId={`otherFinancialCommitments[${index}]description`}
                          required={true}
                          {...register(`otherFinancialCommitments.${index}.description`, { required: 'This field is required' })}
                          // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                          error={errors.otherFinancialCommitments?.[index]?.description}
                        />
                      </div>
                    </div>
                  </div>
                  <Input
                    label='Limit'
                    placeholder='0 *'
                    inputId={`otherFinancialCommitments[${index}]amount`}
                    required={true}
                    money={true}
                    {...register(`otherFinancialCommitments.${index}.amount`, {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                    error={errors.otherFinancialCommitments?.[index]?.amount}
                  />
                  <Input
                    label='Monthly payment'
                    placeholder='0 *'
                    inputId={`otherFinancialCommitments[${index}]monthlyPayments`}
                    required={true}
                    money={true}
                    {...register(`otherFinancialCommitments.${index}.monthlyPayments`, {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    onChange={(e) => {
                      setValue(`otherFinancialCommitments.${index}.monthlyPayments`, e.target.value)
                      calculateTotalMonthlyPayments()
                    }}
                    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                    error={errors.otherFinancialCommitments?.[index]?.monthlyPayments}
                  />
                  <button
                    type='button'
                    className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full absolute z-10 bottom-4 right-0 p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)]'
                    onClick={() => removeFinancialCommitments(index)}
                  >
                    <MinusCircleIcon className='w-6 h-6' />
                  </button>
                </div>
              ))}

              <div className='flex justify-end pt-2 mb-0'>
                <Button
                  icon='add'
                  style='text'
                  onClick={appendOtherFinancialCommitments}
                >Add new</Button>
              </div>

              <div className='flex bg-[var(--theme-bg)] py-8 px-6 items-baseline justify-between mt-8 -mb-4'>
                <p className='text-lg/7 grow'>Total monthly payments</p>
                <p className='font-bold text-lg/7'>$&nbsp;{totalMonthlyPayments.toFixed(2)}</p>
              </div>
            </div>
          </Box>

          {/* Please leave on one line */}
          <Heading><span>What is your {isJointApplication && <span>combined</span>} recurring household <span className='whitespace-nowrap'>expenditure <TooltipIcon id='tooltip-holder' content='Tell us about any fixed or ongoing expenses you have. These are expenses you incur regularly, are committed to paying or cannot choose to stop paying.' /></span></span></Heading>
          <Box>
            <div>
              <div className='grid grid-cols-1 lg:grid-cols-4 border-b lg:gap-8 border-[var(--form-box-divider)] justify-end pt-12 mb-0 max-lg:hidden'>
                <p className='col-start-4 pb-3 text-sm font-[var(--font-weight-bold)]'>Monthly payments</p>
              </div>
              {ownsHome && (
                <>
                  <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                    <div className='lg:col-span-3 pt-2'>
                      <div className='flex w-full items-center gap-5'>
                        <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                          {!isDealer ?
                            <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/rates.svg' aria-label='Rates'/>
                            :
                            <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('councilRates', '/icons/financial-position/rates.svg')} aria-label='Rates'/>
                          }
                        </span>
                        <p className='text-lg/7 mt-0.5'>Council rates <TooltipIcon id='tooltip-holder' content='These are the rates you pay to your local council.' /></p>
                      </div>
                    </div>
                    <Input
                      label='Monthly payments'
                      placeholder='0'
                      inputId='councilRatesExpenditureValue'
                      money={true}
                      {...register('councilRatesExpenditureValue', {
                        validate: (value) => validateMoney(false, value)
                      })}
                      error={errors.councilRatesExpenditureValue}
                    />
                  </div>

                  <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                    <div className='lg:col-span-3 pt-2'>
                      <div className='flex w-full items-center gap-5'>
                        <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                          {!isDealer ?
                            <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/home-insurance.svg' aria-label='Home Insurance'/>
                            :
                            <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('homeInsurance', '/icons/financial-position/home-insurance.svg')} aria-label='Home Insurance'/>
                          }
                        </span>
                        <p className='text-lg/7 mt-0.5'>Home insurance <TooltipIcon id='tooltip-holder' content='Please include any home and contents insurance you have.' /></p>
                      </div>
                    </div>
                    <Input
                      label='Monthly payments'
                      placeholder='0'
                      inputId='homeInsuranceExpenditureValue'
                      money={true}
                      {...register('homeInsuranceExpenditureValue', {
                        validate: (value) => validateMoney(false, value)
                      })}
                      error={errors.homeInsuranceExpenditureValue}
                    />
                  </div>
                </>
              )}

              <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-3 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/utilities.svg' aria-label='Utilities'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('utilities', '/icons/financial-position/utilities.svg')} aria-label='Utilities'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Utilities <TooltipIcon id='tooltip-holder' content='Includes electricity, gas, phone and internet services.' /></p>
                  </div>
                </div>
                <Input
                  label='Monthly payments'
                  placeholder='0'
                  inputId='utilitiesExpenditureValue'
                  money={true}
                  {...register('utilitiesExpenditureValue', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.utilitiesExpenditureValue}
                />
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-3 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/living-expenses.svg' aria-label='Living Expenses'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('livingExpenses', '/icons/financial-position/living-expenses.svg')} aria-label='Living Expenses'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Living expenses <TooltipIcon id='tooltip-holder' content='Includes your day to day living expenses including food, clothing, health and other household expenses.' /></p>
                  </div>
                </div>
                <Input
                  label='Monthly payments'
                  placeholder='0'
                  inputId='livingExpenditureValue'
                  money={true}
                  {...register('livingExpenditureValue', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.livingExpenditureValue}
                />
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg border-b border-[var(--form-box-divider)] pt-2 px-3 lg:px-0 pb-8 lg:pb-4 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-3 pt-2'>
                  <div className='flex w-full items-center gap-5'>
                    <span className='size-8 flex items-center justify-center text-[var(--theme-icon)]'>
                      {!isDealer ?
                        <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/motor-vehicle.svg' aria-label='Motor Vehicle'/>
                        :
                        <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src={getSVGIcon('vehicles', '/icons/financial-position/motor-vehicle.svg')} aria-label='Motor Vehicle'/>
                      }
                    </span>
                    <p className='text-lg/7 mt-0.5'>Motor vehicle <TooltipIcon id='tooltip-holder' content='Includes all expenses related to your vehicle(s) including insurance, WOF, registration, fuel, servicing and maintenance.' /></p>
                  </div>
                </div>
                <Input
                  label='Monthly payments'
                  placeholder='0'
                  inputId='vehicleExpenditureValue'
                  money={true}
                  {...register('vehicleExpenditureValue', {
                    validate: (value) => validateMoney(false, value)
                  })}
                  error={errors.vehicleExpenditureValue}
                />
              </div>

              <div className='grid relative grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 _hide-label-lg pt-2 px-3 lg:px-0 pb-16 mb-2 last:mb-0 lg:mb-0'>
                <div className='lg:col-span-3 pt-2 lg:-mb-3'>
                  <p className='text-base/6 mt-0.5 pl-[54px] lg:pr-6'>
                    Tell us about any other recurring expenses you have that you cannot give up (e.g. child support, child care, other insurances, tithing)
                  </p>
                </div>
              </div>

              {recurringExpensesFields.map((field, index) => (
                <div key={field.id} className='grid relative grid-cols-1 lg:grid-cols-4 gap-5 lg:gap-8 border-b border-[var(--form-box-divider)] _hide-label-lg pt-2 px-3 lg:px-0 pb-16 mb-2 last:mb-0 lg:mb-0'>
                  <div className='lg:col-span-3 pt-2'>
                    <div className='flex w-full items-center gap-5'>
                      <span className='size-8 flex items-center justify-center max-lg:mt-6 text-[var(--theme-icon)]'>
                        {!isDealer ?
                          <ReactSVG className='size-full flex items-center justify-center' src='/icons/financial-position/udc/menu.svg' aria-label='true'/>
                          :
                          <ReactSVG className='FinancialPosition-svg size-full flex items-center justify-center' src='/icons/financial-position/menu.svg' aria-hidden='true'/>
                        }
                      </span>
                      <div className='grow -mt-2'>
                        <Select
                          label='Asset'
                          required={true}
                          selectOptions={recurringExpenseTypeOptions}
                          defaultValue={state.personal.financialPosition.otherRecurringExpenses[index]?.expenseType}
                          onChange={value => handleSelectChange(`otherRecurringExpenses.${index}.expenseType`, value)}
                          // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                          error={errors.otherRecurringExpenses?.[index]?.expenseType}
                        />
                      </div>
                    </div>
                  </div>
                  <Input
                    label='Monthly payment'
                    placeholder='0 *'
                    inputId={`otherRecurringExpenses[${index}]monthlyPayments`}
                    required={true}
                    money={true}
                    {...register(`otherRecurringExpenses.${index}.monthlyPayments`, {
                      required: 'This field is required',
                      validate: (value) => validateMoney(true, value)
                    })}
                    onChange={(e) => {
                      setValue(`otherRecurringExpenses.${index}.monthlyPayments`, e.target.value)
                      calculateTotalRecurringMonthlyPayments()
                    }}
                    // @ts-expect-error - We know it's one possible value of the form, but we don't want to create yet another type for this
                    error={errors.otherRecurringExpenses?.[index]?.monthlyPayments}
                  />
                  <button
                    className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full absolute z-10 bottom-4 right-0 p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)]'
                    type='button'
                    onClick={() => removeRecurringExpenses(index)}
                  >
                    <MinusCircleIcon className='w-6 h-6' />
                  </button>
                </div>
              ))}
              
              <div className='flex justify-end pt-2 mb-0'>
                <Button
                  icon='add'
                  style='text'
                  onClick={appendAndRegisterRecurringExpenses}
                >Add new</Button>
              </div>

              <div className='flex bg-[var(--theme-bg)] py-8 px-6 items-baseline justify-between mt-8 -mb-4'>
                <p className='text-lg/7 grow'>Total monthly payments</p>
                <p className='font-bold text-lg/7'>$&nbsp;{totalRecurringMonthlyPayments.toFixed(2)}</p>
              </div>
            </div>
          </Box>

          <Box>
            <div>
              <Textarea
                label='Notes'
                inputId='additionalNotes'
                placeholder='Notes'
                {...register('additionalNotes')}
                error={errors.additionalNotes}
              />
            </div>
          </Box>

          {state.isPostCall && (
            <div className="my-8 max-w-2xl mx-auto bg-white border border-gray-300 rounded-lg shadow-sm p-6 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <input
                  id="post-call-confirm-checkbox"
                  type="checkbox"
                  className="mt-1 accent-[var(--brand-1)] w-5 h-5 border-gray-400 rounded focus:ring-2 focus:ring-[var(--brand-1)]"
                  checked={isPostCallChecked}
                  onChange={e => setIsPostCallChecked(e.target.checked)}
                />
                <div className="text-sm text-gray-700">
                  <span className="font-semibold block mb-2">By ticking this box, you confirm that:</span>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>You have checked all information automatically entered into the application and updated where necessary.</li>
                    <li>You are not currently bankrupt or have a history of credit defaults.</li>
                    <li>You confirm the UDC Privacy Statement applies to your application.</li>
                    <li>Your application is subject to UDC's credit approval process. Lending criteria applied.</li>
                  </ul>
                </div>
              </div>
              {isPostCallError && (
                <p className="text-red-600 text-sm font-medium mt-1">You must confirm the above before submitting your application.</p>
              )}
            </div>
          )}

          <div className='flex flex-col justify-center items-center py-10 pb-12 max-w-5xl mx-auto'>
            <div className={`inline-flex justify-center rounded-md border p-0.5 ${ isRecaptchaError ? 'border-[var(--const-error)]' : 'border-[transparent]'}`} style={{opacity: isLoading ? 0 : 1}}>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey="6LdAhscUAAAAAGNb6RwOaBoOh7TWi50a0-iwg-sE"
                onChange={(token: string | null) => {
                  setRecaptchaToken(token)
                  setIsRecaptchaError(false)
                }}
                onExpired={() => setRecaptchaToken(null)}
                onErrored={() => {
                  window.alert('We are sorry, there was an error with the reCAPTCHA. Please try again.')
                  setRecaptchaToken(null)
                  recaptchaRef.current?.reset()
                }}
              />
            </div>
            <p className={`text-sm text-[var(--const-error)] text-center pt-4 ${ isRecaptchaError ? 'block' : 'hidden'}`}>Please verify that you are human by clicking the reCAPTCHA checkbox</p>
          </div>
          <div className='flex justify-between mt-8 pb-12 max-w-5xl mx-auto'>
            <Button iconPosition='left' onClick={() => navigate(ROUTES.PERSONAL.INCOME.path)}>Back</Button>
            <Button
              type='submit'
              onClick={() => setSubmitAction('submit')}
            >Submit</Button>
          </div>
          <Box>
            <h3 className='py-1.5 text-center max-w-xl mx-auto text-2xl md:text-3xl text-[var(--theme-heading-text)] leading-[1.2]'>You can speed up your application with APLYiD and document upload (optional):</h3>
            <div className='flex justify-center'>
              <Button
                type='submit'
                onClick={() => setSubmitAction('continue')}
              >Continue</Button>
            </div>
          </Box>
        </section>
      </form>

      <PageLoader open={isLoading} />
    </>
  )
}

export default FinancialPosition
